import { faPlayCircle } from '@fortawesome/pro-light-svg-icons';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, Icon, SpinnerIcon } from 'corso-admin-ui';
import { match } from 'ts-pattern';
import { KnownMediaContentType } from './useMediaContentType';

export type MediaFallbackType =
  | 'loading'
  | 'error'
  | KnownMediaContentType
  | null;

const mediaFallbackVariants = cva('flex items-center justify-center', {
  variants: {
    variant: {
      panel: 'size-full',
      chip: 'size-16 rounded-md p-2',
    },
    tone: {
      light: null,
      dark: null,
    },
  },
  compoundVariants: [
    {
      variant: 'chip',
      tone: 'light',
      className: 'border border-neutral-100 bg-neutral-50 text-neutral-600',
    },
    {
      variant: 'chip',
      tone: 'dark',
      className: 'bg-neutral-600 text-neutral-200',
    },
  ],
  defaultVariants: {
    variant: 'panel',
    tone: 'light',
  },
});

export function MediaFallback({
  type,
  alt,
  variant,
  tone,
  className,
}: VariantProps<typeof mediaFallbackVariants> & {
  type: MediaFallbackType;
  alt?: string;
  className?: string;
}) {
  return (
    <div
      data-fallback={type}
      className={cn(mediaFallbackVariants({ variant, tone }), className)}
    >
      {match(type)
        .with('loading', () => <SpinnerIcon accessibilityLabel={alt} />)
        .with('error', () => (
          <Icon
            icon={variant === 'chip' ? 'errorInline' : 'error'}
            className="text-red-600"
            accessibilityLabel={alt}
          />
        ))
        .with('video', () => (
          <Icon
            icon={faPlayCircle}
            className="size-6"
            accessibilityLabel={alt}
          />
        ))
        .with('pdf', () => (
          <Icon icon="filePdf" className="size-6" accessibilityLabel={alt} />
        ))
        .with('image', null, () => (
          <Icon icon="file" className="size-6" accessibilityLabel={alt} />
        ))
        .exhaustive()}
    </div>
  );
}
